/**
 * NPC dialog session state (S7).
 *
 * Dialog content and every branch decision live on the backend — `npcs-dialog.md`
 * rule 7 is explicit: "No client-side branching." This module holds only the
 * node the server last sent plus request status; it never picks a next node, and
 * it never decides whether a reply is available.
 *
 * ─── What is still client-side, correctly ───────────────────────────────────
 * Proximity detection and the "Press E" prompt stay in Base View's sim (the
 * GDD's Settlement/Base View row assigns them there). The sim also still owns
 * the local `world.dialog` tree, which is now used ONLY for its own
 * prompt-state bookkeeping and validation tests — no longer for rendering. That
 * duplication is a known follow-up, flagged in sprint-2.md: retiring
 * `BaseSim.chooseOption`/`currentDialog` means retiring their tests too, which
 * is a separate change from moving the source of truth.
 */
import { createStore } from '../../core/state/store';
import { api } from '../../core/network/api';

export interface NpcSummary {
  key: string;
  name: string;
  role: string;
  location: string;
  portrait: string;
}

export interface DialogReplyView {
  index: number;
  text: string;
  /** Server-stated: choosing this reply ends the conversation. */
  ends: boolean;
  /** Server-derived: what the reply does, for its icon. Absent for plain talk. */
  kind?: 'trade' | 'mission' | 'accept';
  /** Visible but not choosable yet — `reason` is what is missing. */
  locked?: { reason: string };
}

export interface DialogNodeView {
  nodeId: string;
  npcKey: string;
  text: string;
  replies: DialogReplyView[];
  /** A quest brief's "what needs doing" card. */
  card?: { title: string; objectives: string[]; reward: string };
}

/**
 * `POST /npc/:key/reply` response. `ended` is explicit rather than inferred from
 * a null node — a bare `null` return serializes to an empty body, which can't be
 * told apart from a failed request (found while driving S7 live).
 */
interface DialogReplyResult {
  ended: boolean;
  node: DialogNodeView | null;
}

export interface NpcState {
  /** Catalog from `GET /npc`. Empty until loaded; static content, fetched once. */
  npcs: NpcSummary[];
  /** The node currently on screen, or null when no conversation is open. */
  node: DialogNodeView | null;
  /** NPC key of the open conversation — kept separately so it survives a node fetch. */
  activeNpcKey: string | null;
  /** True while a dialog/reply request is in flight — used to lock replies against double-clicks. */
  busy: boolean;
  error: string | null;
}

const INITIAL_STATE: NpcState = {
  npcs: [], node: null, activeNpcKey: null, busy: false, error: null,
};

export const npcStateStore = createStore<NpcState>({ ...INITIAL_STATE });

/** Fetches the NPC catalog once. Safe to call on every settlement entry. */
export async function loadNpcs(): Promise<void> {
  if (npcStateStore.get().npcs.length > 0) return;
  try {
    const npcs = await api.get<NpcSummary[]>('/npc');
    npcStateStore.set((s) => ({ ...s, npcs, error: null }));
  } catch (err) {
    npcStateStore.set((s) => ({ ...s, error: msg(err, 'Failed to load NPCs') }));
  }
}

/** Opens a conversation. The server applies milestone gating and returns the starting node. */
export async function openDialog(npcKey: string): Promise<DialogNodeView | null> {
  npcStateStore.set((s) => ({
    ...s, busy: true, error: null, activeNpcKey: npcKey,
  }));
  try {
    const node = await api.post<DialogNodeView>(`/npc/${npcKey}/dialog`, {});
    npcStateStore.set((s) => ({ ...s, node, busy: false }));
    return node;
  } catch (err) {
    // Edge case 5: an authoring error surfaces as DIALOG_UNAVAILABLE. Close the
    // session rather than leaving a half-open panel with no node.
    npcStateStore.set((s) => ({
      ...s, node: null, activeNpcKey: null, busy: false, error: msg(err, 'Couldn\'t start conversation.'),
    }));
    return null;
  }
}

/**
 * Sends a reply and stores whatever node comes back.
 *
 * Returns the next node, or null when the conversation ended — callers use that
 * to close the panel. `busy` gates re-entry: edge case 3 says the server is
 * idempotent about double-claims, but there's no reason to send the second click
 * at all.
 */
export async function chooseReply(replyIndex: number): Promise<DialogNodeView | null> {
  const { activeNpcKey, node, busy } = npcStateStore.get();
  if (!activeNpcKey || !node || busy) return node;

  npcStateStore.set((s) => ({ ...s, busy: true, error: null }));
  try {
    const result = await api.post<DialogReplyResult>(`/npc/${activeNpcKey}/reply`, {
      nodeId: node.nodeId,
      replyIndex,
    });
    if (result.ended || !result.node) {
      npcStateStore.set((s) => ({
        ...s, node: null, activeNpcKey: null, busy: false,
      }));
      return null;
    }
    npcStateStore.set((s) => ({ ...s, node: result.node, busy: false }));
    return result.node;
  } catch (err) {
    npcStateStore.set((s) => ({ ...s, busy: false, error: msg(err, 'Couldn\'t continue conversation.') }));
    return npcStateStore.get().node;
  }
}

/** Closes the conversation client-side. Dialog is stateless server-side, so there is nothing to tell it. */
export function closeDialog(): void {
  npcStateStore.set((s) => ({
    ...s, node: null, activeNpcKey: null, busy: false, error: null,
  }));
}

/** Full reset — called on sign-out so a second account never sees the first one's session. */
export function resetNpcState(): void {
  npcStateStore.set({ ...INITIAL_STATE });
}

function msg(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
